// FE-SCRM-060 — <Pagination> + ?page= / ?page_size= URL codec.
//
// Example:
//
//     // Inside any list panel
//     <Pagination total={response.total_count} />
//     // The URL is the single source of truth; consumer reads ?page,
//     // ?page_size off the URL and re-fetches the list query.
//
// Mounts inline on every Phase-7b list (Prospects, Opportunities,
// Contacts). Page-size choices fixed at 25 / 50 / 100 — anything
// else is silently coerced back to the default so a manual URL
// can't sneak ``page_size=10000`` past the BE pagination cap.

import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";


export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
export type PageSize = (typeof PAGE_SIZE_OPTIONS)[number];

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE: PageSize = 50;


export interface PaginationState {
  page: number;
  pageSize: PageSize;
}


export function readPaginationFromUrl(
  params: URLSearchParams,
): PaginationState {
  const rawPage = Number.parseInt(params.get("page") ?? "", 10);
  const page = Number.isFinite(rawPage) && rawPage >= 1
    ? rawPage : DEFAULT_PAGE;

  const rawSize = Number.parseInt(params.get("page_size") ?? "", 10);
  const pageSize: PageSize =
    (PAGE_SIZE_OPTIONS as readonly number[]).includes(rawSize)
      ? (rawSize as PageSize) : DEFAULT_PAGE_SIZE;

  return { page, pageSize };
}


export function writePaginationToParams(
  state: PaginationState,
  base: URLSearchParams,
): URLSearchParams {
  // Clone so we don't mutate the caller's params.
  const out = new URLSearchParams(base);
  out.delete("page");
  out.delete("page_size");
  if (state.page !== DEFAULT_PAGE) out.set("page", String(state.page));
  if (state.pageSize !== DEFAULT_PAGE_SIZE) {
    out.set("page_size", String(state.pageSize));
  }
  return out;
}


export interface PaginationProps {
  total: number;
  className?: string;
}


export function Pagination({
  total, className,
}: PaginationProps): JSX.Element | null {
  const [searchParams, setSearchParams] = useSearchParams();
  const { page, pageSize } = readPaginationFromUrl(searchParams);

  const update = useCallback(
    (next: PaginationState) => {
      setSearchParams(
        writePaginationToParams(next, searchParams),
        { replace: false },
      );
    },
    [searchParams, setSearchParams],
  );

  if (total <= PAGE_SIZE_OPTIONS[0]) {
    // Below the smallest page size — nothing to paginate.
    return null;
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);

  return (
    <nav
      className={
        "flex items-center justify-between gap-4 py-3 text-sm "
        + (className ?? "")
      }
      aria-label="Pagination"
    >
      <label className="flex items-center gap-2 text-gray-700">
        <span>Rows per page</span>
        <select
          aria-label="Rows per page"
          value={pageSize}
          onChange={(e) => {
            const next = Number.parseInt(e.target.value, 10) as PageSize;
            // Reset page so we never land past the new total-pages cap.
            update({ page: DEFAULT_PAGE, pageSize: next });
          }}
          className={
            "rounded-md border border-gray-300 bg-white px-2 py-1 "
            + "text-sm focus:border-indigo-500 focus:outline-none "
            + "focus:ring-1 focus:ring-indigo-500"
          }
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>
      </label>

      <div className="flex items-center gap-3 text-gray-700">
        <span>Page {safePage} of {totalPages}</span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Previous page"
            disabled={safePage <= 1}
            onClick={() => update({ page: safePage - 1, pageSize })}
            className={
              "rounded-md border border-gray-300 px-2 py-1 "
              + "text-sm enabled:hover:bg-gray-50 "
              + "disabled:cursor-not-allowed disabled:opacity-50"
            }
          >
            ‹ Prev
          </button>
          <button
            type="button"
            aria-label="Next page"
            disabled={safePage >= totalPages}
            onClick={() => update({ page: safePage + 1, pageSize })}
            className={
              "rounded-md border border-gray-300 px-2 py-1 "
              + "text-sm enabled:hover:bg-gray-50 "
              + "disabled:cursor-not-allowed disabled:opacity-50"
            }
          >
            Next ›
          </button>
        </div>
      </div>
    </nav>
  );
}
